import { Component, Input, OnDestroy, OnInit, signal, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ModalService } from '@meplis/services';
import { ProfileAvatarComponent } from '../../../profile/profile-avatar/profile-avatar.component';
import { MatMenuModule } from '@angular/material/menu';
import { ProfileWidgetComponent } from '../../../profile/profile-widget/profile-widget.component';
import { HubMenuComponent } from '../hub-menu/hub-menu.component';
import { NotificationMenuComponent } from '../../../notifications/notification-menu/notification-menu.component';
import {
  AccountType,
  EntityInstance,
  Hub,
  SocialNetworkInstanceImageType,
} from '@meplis/common';
import { SocialService } from 'apps/meplis-cnp/src/app/shared/services/application/social.service';
import { CommonService } from 'apps/meplis-cnp/src/app/shared/services/application/common.service';
import { forkJoin, Subscription, switchMap } from 'rxjs';
import { ProfileService } from '@cnp/services';
import { AuthenticationService } from 'apps/meplis-cnp/src/app/shared/services/application/authentication.service';
import { HubService } from 'apps/meplis-cnp/src/app/shared/services/application/hub.service';
import { SocialNetworkInstanceImage } from 'libs/meplis/src/lib/common/models/social-network/social-network-instance-image';
import { ApiSocialV2Service } from '@cnp/services';
import { ChatMenuComponent } from '../../../chat/chat-menu/chat-menu.component';

@Component({
  selector: 'app-header-workspace',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    ProfileAvatarComponent,
    ProfileWidgetComponent,
    MatMenuModule,
    HubMenuComponent,
    NotificationMenuComponent,
    ChatMenuComponent,
  ],
  templateUrl: './header-workspace.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './header-workspace.component.scss',
})
export class HeaderWorkspaceComponent implements OnInit, OnDestroy {
  private _commonService = inject(CommonService);
  private _router = inject(Router);
  private _socialService = inject(SocialService);
  private _profileService = inject(ProfileService);
  private _authService = inject(AuthenticationService);
  private _hubCnpService = inject(HubService);

  @Input() public userInstance: EntityInstance;

  public logoUrl: string = 'images/meplis-logo.webp';
  public profileId: number;
  /** Comes back on profile reads, not `instances/me` -- see `showExploreMeplis`. */
  public accountType: AccountType;
  public currentHub: Hub;
  public hubLogoUrl: string;
  public logoImage: SocialNetworkInstanceImage;

  public currentRoute: string;
  public socialInstanceId: string;
  public imageTypes = SocialNetworkInstanceImageType;

  public isUnlocking: boolean = false;

  public isMobile = signal(false);
  public mobileChangeSubscription: Subscription;
  public routeChangeSubscription: Subscription;
  public authSubscription: Subscription;
  public profileSubscription: Subscription;
  public userInstanceSubscription: Subscription;
  public unlockSubscription: Subscription;
  public profileChangedSubscription: Subscription;

  constructor() {
    this._handleMobileChanges();
  }

  public ngOnInit(): void {
    this._handleInit();
    this.currentRoute = this._router.url.replace(/^\//, '');

    this.authSubscription = this._authService.onLoggedInOut
      .pipe(switchMap(() => this._socialService.getUserInstance()))
      .subscribe((userInstance) => {
        this.userInstance = userInstance;
        this._handleInit();
      });

    // Listen for unlock animation trigger
    this.unlockSubscription = this._socialService
      .onUnlockStarted()
      .subscribe(() => {
        this.isUnlocking = true;
      });

    this.profileChangedSubscription = this._profileService.onProfileChanged
      .pipe(
        switchMap(() => {
          this._socialService.clearUserInstance();
          return forkJoin({
            profile: this._profileService.getProfile(),
            userInstance: this._socialService.getUserInstance(),
          });
        })
      )
      .subscribe(({ profile, userInstance }) => {
        this.profileId = profile.id;
        this.accountType = profile.accountType;
        this.userInstance = userInstance;
      });
  }

  ngOnDestroy(): void {
    this.mobileChangeSubscription?.unsubscribe();
    this.routeChangeSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
    this.profileSubscription?.unsubscribe();
    this.userInstanceSubscription?.unsubscribe();
    this.unlockSubscription?.unsubscribe();
    this.profileChangedSubscription?.unsubscribe();
  }

  private _handleInit(): void {
    this.routeChangeSubscription = this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.urlAfterRedirects.replace(/^\//, '');
      }
    });

    if (!this.isAuthenticated()) {
      return;
    }

    this.profileSubscription = this._profileService
      .getProfile()
      .subscribe((profile) => {
        this.profileId = profile.id;
        this.accountType = profile.accountType;
      });

    this.userInstanceSubscription = this._socialService
      .getUserInstance()
      .subscribe((result) => {
        if (this.userInstance?.sourceHubId) {
          this._hubCnpService
            .getHub(this.userInstance.sourceHubId)
            .subscribe((hub) => {
              this.currentHub = hub;
              this.logoImage = this.currentHub.instance.images?.find(
                (img) => img.type === SocialNetworkInstanceImageType.LOGO
              );
              this.hubLogoUrl =
                this.logoImage?.file?.url || 'https://via.placeholder.com/20';
            });
        }
      });
  }

  public isAuthenticated(): boolean {
    return this._authService.isLogged;
  }

  /** "Explore Meplis" offers to unlock the whole app -- an industry rep
   * invited into a single hub shouldn't see a path to the rest of it.
   * `accountType` comes from the profile, not `userInstance` -- the
   * instances/me endpoint doesn't carry it. */
  public get showExploreMeplis(): boolean {
    return this.accountType !== AccountType.INDUSTRY;
  }

  public goToLogin(): void {
    this._router.navigate(['/login']);
  }

  public goToSignUp(): void {
    this._router.navigate(['/sign-up']);
  }

  private _handleMobileChanges(): void {
    const updateTabs = (isMobile: boolean) => {
      this.isMobile.set(isMobile);
    };

    updateTabs(this._commonService.isMobile);

    this.mobileChangeSubscription =
      this._commonService.onIsMobileChange.subscribe(() => {
        updateTabs(this._commonService.isMobile);
      });
  }
}
